import {
  PartialQuestionnaire,
  QuestionnaireQuestions,
  Rule,
} from '../../models';
import { findDependents } from '../findDependents';
import { filterRequiredAnswers } from './filterRequiredAnswers';
import { removeDependentAnswers } from './removeDependentAnswers';
import { StateManager } from './StateManager';

export const useStateManager = <
  Questionnaire extends QuestionnaireQuestions,
  GroupId,
  ExtendedComponentTypes
>(
  config: Partial<Record<keyof Questionnaire, RemoveAnswerType<Questionnaire>>>,
  questionnaire: PartialQuestionnaire<
    Questionnaire,
    GroupId,
    ExtendedComponentTypes
  >,
  rules: Rule<Questionnaire>[]
) => {
  return StateManager(config, questionnaire, rules);
};

export type RemoveAnswerType<Questionnaire extends QuestionnaireQuestions> = {
  op: 'always';
  questions: Array<keyof Questionnaire>;
};

export const stateManagerHelper = <
  Questionnaire extends QuestionnaireQuestions,
  GroupId,
  ExtendedComponentTypes
>(
  config: Partial<Record<keyof Questionnaire, RemoveAnswerType<Questionnaire>>>,
  questionnaire: PartialQuestionnaire<
    Questionnaire,
    GroupId,
    ExtendedComponentTypes
  >,
  answers: Partial<Questionnaire>,
  rules: Rule<Questionnaire>[]
) => {
  let state = { ...answers };
  const manager = {
    set: <QuestionId extends keyof Questionnaire>(
      questionId: QuestionId,
      answer: Questionnaire[QuestionId],
      autoRemove: boolean = true,
      autoFilterRequiredAnswers = false
    ) => {
      // remove any questions depending on the current question if there is a state change
      const previousAnswer = state[questionId];
      const hasChange = answer !== previousAnswer;
      const answersToRemove = config[questionId]?.questions;
      if (hasChange && autoRemove && answersToRemove !== undefined) {
        const filteredState = Object.entries(state).reduce(
          (acc, [questionId, answer]) => {
            if (answersToRemove.includes(questionId)) {
              return acc;
            }
            return { ...acc, [questionId]: answer };
          },
          {}
        );
        state = filteredState;
      }

      state = {
        ...state,
        [questionId]: answer,
      };

      state =
        hasChange && autoRemove
          ? removeDependentAnswers(questionnaire, rules, state, questionId)
          : state;

      state = autoFilterRequiredAnswers
        ? filterRequiredAnswers(questionnaire, rules, state)
        : state;

      return manager;
    },
    getAnswersToRemove: <QuestionId extends keyof Questionnaire>(
      questionId: QuestionId,
      answer: Questionnaire[QuestionId]
    ) => {
      // collect any questions to remove depending on the current question and if there is a state change
      const previousAnswer = state[questionId];
      const hasChange = answer !== previousAnswer;
      if (!hasChange) {
        return [];
      }

      let removableAnswers: (keyof Questionnaire)[] = [];
      const answersToRemove = config[questionId]?.questions;
      if (answersToRemove !== undefined) {
        removableAnswers = answersToRemove;
      }

      return [
        ...new Set([
          ...removableAnswers,
          ...findDependents(questionnaire, rules, questionId),
        ]),
      ];
    },
    getAnswers: () => {
      return state;
    },
  };

  return manager;
};

export const removeAnswers = <Questionnaire extends QuestionnaireQuestions>(
  answers: Partial<Questionnaire>,
  toRemove: (keyof Questionnaire)[]
) => {
  return Object.entries(answers).reduce<Partial<Questionnaire>>(
    (obj, [key, value]) => {
      if (!toRemove.includes(key as keyof Questionnaire)) {
        return { ...obj, [key]: value };
      }
      return { ...obj, [key]: undefined };
    },
    {}
  );
};
